import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { NewSettingsForm } from "../../forms/NewSettingsForm/NewSettingsForm";
import { ParamSettings } from "../../models/ParamSettings";
import { getAllSettings } from "../../services/SettingsService";
import { cornParams, cornParams2 } from "../../mock/SettingsData";
import "./SettingsPage.scss";

export const SettingsPage = () => {
  const { t } = useTranslation();

  const [newParamSettings, setNewParamSettings] = useState<ParamSettings[]>([]);

  useEffect(() => {
    getAllSettings()
      .then((resp) => {
        if (resp?.data?.status === "ERROR") {
          throw new Error(resp?.data?.message ?? "Greška");
        }
        setNewParamSettings(resp?.data?.result ?? []);
      })
      .catch((e: any) => {
        console.error(e);
        setNewParamSettings([]);
      });
  }, []);

  const renderMarginalHeader = () => {
    return (
      <div className="marginal-bonification-header">
        <span>{t("Poljoprivredna kultura")}</span>
        <span>{t("Vlaga")}</span>
        <span>{t("Primese")}</span>
        <span>{t("Hektolitar")}</span>
        <span>{t("Lom")}</span>
        <span>{t("Defekt")}</span>
        <span>{t("Opcije")}</span>
      </div>
    );
  };

  const settingsName = (rowData: any) => {
    return rowData.mercantile;
  };

  const breakageValue = (rowData: any) => {
    return rowData.breakage != 0 ? (
      <span>{rowData.breakage}</span>
    ) : (
      <span></span>
    );
  };

  const defectValue = (rowData: any) => {
    return rowData.defect != 0 ? <span>{rowData.defect}</span> : <span></span>;
  };

  const hectoliterValue = (rowData: any) => {
    return rowData.hectoliter != 0 ? (
      <span>{rowData.hectoliter}</span>
    ) : (
      <span></span>
    );
  };

  const hectoliterMinValue = (rowData: any) => {
    return rowData.hectoliterMin != 0 ? (
      <span>{rowData.hectoliterMin}</span>
    ) : (
      <span></span>
    );
  };

  const hectoliterMaxValue = (rowData: any) => {
    return rowData.hectoliterMax != 0 ? (
      <span>{rowData.hectoliterMax}</span>
    ) : (
      <span></span>
    );
  };

  const breakageMinValue = (rowData: any) => {
    return rowData.breakageMin != 0 ? (
      <span>{rowData.breakageMin}</span>
    ) : (
      <span></span>
    );
  };

  const breakageMaxValue = (rowData: any) => {
    return rowData.breakageMax != 0 ? (
      <span>{rowData.breakageMax}</span>
    ) : (
      <span></span>
    );
  };

  const defectMinValue = (rowData: any) => {
    return rowData.defectMin != 0 ? (
      <span>{rowData.defectMin}</span>
    ) : (
      <span></span>
    );
  };

  const defectMaxValue = (rowData: any) => {
    return rowData.defectMax != 0 ? (
      <span>{rowData.defectMax}</span>
    ) : (
      <span></span>
    );
  };

  const handleNewParamSettings = (newSettings: ParamSettings) => {
    let copyOfNewParamSettings = cloneDeep(newParamSettings);
    const paramIndex = newParamSettings.findIndex(
      (item) => item.id === newSettings.id
    );
    copyOfNewParamSettings[paramIndex] = newSettings;
    setNewParamSettings(copyOfNewParamSettings);
  };

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <NewSettingsForm
        handleNewParamSettings={handleNewParamSettings}
        buttonLabel=""
        buttonIcon="bi bi-pencil"
        settings={rowData}
      />
    );
  };

  const calculateCaloBodyTemplate = (rowData: any) => {
    return <span>{rowData.calculateCalo === true ? t("Da") : t("Ne")}</span>;
  };

  return (
    <PageLayout>
      <div className="settings-page-wrapper">
        <div className="params-calc-section">
          <div className="params-table">
            <div className="table-header">
              <i
                className="bi bi-truck header-title-icon"
                style={{ fontSize: 16, transform: "translate(0, 1px)" }}
              ></i>
              <span className="header-title">{t("SRPS parametri")}</span>
            </div>
            <div className="table-body">
              <DataTable value={newParamSettings} showGridlines stripedRows>
                <Column
                  body={settingsName}
                  header={t("Poljoprivredna kultura")}
                />
                <Column field="moisture" header={t("Vlaga")} />
                <Column field="impurity" header={t("Primese")} />
                <Column body={breakageValue} header={t("Lom")} />
                <Column body={defectValue} header={t("Defekt")} />
                <Column body={hectoliterValue} header={t("Hektolitar")} />
                <Column
                  body={calculateCaloBodyTemplate}
                  header={t("Obračun kalo")}
                />
                <Column
                  body={editActionBodyTemplate}
                  className="datatable-button-column"
                  header={t("Opcije")}
                />
              </DataTable>
            </div>
          </div>
        </div>
        <div className="marginal-bonification-section">
          <div className="marginal-bonification-table">
            <div className="table-header">
              <i
                className="bi bi-truck header-title-icon"
                style={{ fontSize: 16, transform: "translate(0, 1px)" }}
              ></i>
              <span className="header-title">
                {t("Marginalne bonifikacije")}
              </span>
            </div>
            <div className="table-body">
              <DataTable
                header={renderMarginalHeader}
                value={newParamSettings}
                showGridlines
                stripedRows
              >
                <Column body={settingsName} header="" />
                <Column field="moistureMin" header={t("donja")} />
                <Column field="moistureMax" header={t("gornja")} />
                <Column field="impurityMin" header={t("donja")} />
                <Column field="impurityMax" header={t("gornja")} />
                <Column body={hectoliterMinValue} header={t("donja")} />
                <Column body={hectoliterMaxValue} header={t("gornja")} />
                <Column body={breakageMinValue} header={t("donja")} />
                <Column body={breakageMaxValue} header={t("gornja")} />
                <Column body={defectMinValue} header={t("donja")} />
                <Column body={defectMaxValue} header={t("gornja")} />
                <Column
                  body={editActionBodyTemplate}
                  className="datatable-button-column"
                  header="&nbsp;"
                />
              </DataTable>
            </div>
          </div>
        </div>
        <div className="coefficient-corn-section">
          <div className="coefficient-corn-table">
            <div className="table-header">
              <i
                className="bi bi-truck header-title-icon"
                style={{ fontSize: 16, transform: "translate(0, 1px)" }}
              ></i>
              <span className="header-title">
                {t(
                  "Tabela koeficijenata za svodjenje na standardnu vlagu kod kukuruza"
                )}
              </span>
            </div>
            <div className="table-body">
              <DataTable value={cornParams} showGridlines>
                <Column field="range0" header="14.51-15.0" />
                <Column field="range1" header="15.01-15.5" />
                <Column field="range2" header="15.51-16.0" />
                <Column field="range3" header="16.01-16.5" />
                <Column field="range4" header="16.51-17.0" />
                <Column field="range5" header="17.01-17.5" />
                <Column field="range6" header="17.51-18.0" />
                <Column field="range7" header="18.01-18.5" />
                <Column field="range8" header="18.51-19.0" />
                <Column field="range9" header="19.01-19.5" />
                <Column field="range10" header="19.51-20.0" />
                <Column field="range11" header="20.01-20.5" />
                <Column field="range12" header="20.51-21.0" />
                <Column field="range13" header="20.01-21.5" />
                <Column field="range14" header="21.51-22.0" />
                <Column field="range15" header="22.01-22.5" />
                <Column field="range16" header="22.51-23.0" />
                <Column field="range17" header="23.01-23.5" />
                <Column field="range18" header="23.51-24.0" />
              </DataTable>
            </div>
          </div>
        </div>
        <div className="coefficient-corn-section">
          <div className="coefficient-corn-table">
            <div className="table-header">
              <i
                className="bi bi-truck header-title-icon"
                style={{ fontSize: 16, transform: "translate(0, 1px)" }}
              ></i>
              <span className="header-title">
                {t("Tabela koeficijenata za troškove sušenja kod kukuruza")}
              </span>
            </div>
            <div className="table-body">
              <DataTable value={cornParams2} showGridlines>
                <Column field="range0" header="14.51-15.0" />
                <Column field="range1" header="15.01-15.5" />
                <Column field="range2" header="15.51-16.0" />
                <Column field="range3" header="16.01-16.5" />
                <Column field="range4" header="16.51-17.0" />
                <Column field="range5" header="17.01-17.5" />
                <Column field="range6" header="17.51-18.0" />
                <Column field="range7" header="18.01-18.5" />
                <Column field="range8" header="18.51-19.0" />
                <Column field="range9" header="19.01-19.5" />
                <Column field="range10" header="19.51-20.0" />
                <Column field="range11" header="20.01-20.5" />
                <Column field="range12" header="20.51-21.0" />
                <Column field="range13" header="20.01-21.5" />
                <Column field="range14" header="21.51-22.0" />
                <Column field="range15" header="22.01-22.5" />
                <Column field="range16" header="22.51-23.0" />
                <Column field="range17" header="23.01-23.5" />
                <Column field="range18" header="23.51-24.0" />
              </DataTable>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
